import cn from "classnames"
import React, { useMemo } from "react"
import { Control, FieldName } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
    addYears,
    DateInput,
    DropDownList,
    formatShortDateWithFormat,
    getCountriesList,
    getStringLocaleDateFormat,
    InputLabel,
    TitleWithAbsoluteHint,
    useSelector,
} from "swiipe.portal.shared"
import { userSelectors } from "../../../store/userReducer"
import { IFormBase } from "../../../types/form/IFormBase"
import { EGender } from "../../../types/user/EGender"
import { getFullNameFromFirstAndLastName } from "../../../utils/formatUtil"
import {
    filterShouldValidate,
    valFuncRequired,
    valFuncRequiredAndPattern,
    validationPatterns,
} from "../../../utils/validationUtil"
import { FieldsetFormGroup } from "../../form/FieldsetFormGroup"
import { FormInputColumnWithInfoColumn } from "../../form/FormInputColumnWithInfoColumn"
import { FloatingLabelInput } from "../../form/input/FloatingLabelInput"
import { PhoneNumberInput } from "../../form/input/PhoneNumberInput"
import { CompletedFieldsReview } from "../../form/review/CompletedFieldsReview"
import "./AcquiringContactFields.scss"

type BaseSubForm = {
    email?: string
    phone?: string
    gender?: EGender
    dateOfBirth?: string
    country?: string
}

export type IAcquiringContactFullNameSubForm = {
    name: string
} & BaseSubForm

export type IAcquiringContactFirstAndLastNameSubForm = {
    firstName: string
    lastName: string
} & BaseSubForm

export type IAcquiringContactSubForm = IAcquiringContactFullNameSubForm | IAcquiringContactFirstAndLastNameSubForm

interface IContactFieldsProps<TForm extends IFormBase> {
    control: Control<TForm>
    nameType: "fullName" | "firstAndLastName"
    showReview: boolean
    defaultToUserData?: boolean
    getFieldsFromControl: (form: TForm | undefined) => IAcquiringContactSubForm | undefined
    savedFields: IAcquiringContactSubForm | undefined
    fieldSelectorPrefix: string
    validatedFields?: string[]
    viewMode?: boolean
    emailHintText?: string
    isDisabled?: boolean
    isEmailDisabled?: boolean
    showEmail?: boolean
    showPhone?: boolean
    showCountry?: boolean
    showGender?: boolean
    showDateOfBirth?: boolean
    useInputLabels?: boolean
}

export const AcquiringContactFields = <TForm extends IFormBase>({
    control,
    nameType,
    showReview,
    defaultToUserData,
    getFieldsFromControl,
    savedFields,
    fieldSelectorPrefix,
    validatedFields,
    viewMode,
    emailHintText,
    isDisabled,
    isEmailDisabled,
    showEmail,
    showPhone,
    showGender,
    showDateOfBirth,
    showCountry,
    useInputLabels,
}: IContactFieldsProps<TForm>) => {
    const { t } = useTranslation()
    const userDataFromState = useSelector(userSelectors.userData)
    const userData = defaultToUserData ? userDataFromState : undefined
    const currentFields = (!viewMode && getFieldsFromControl(control.getValues() as TForm)) || undefined

    const getFieldSelector = (fieldName: string) => fieldSelectorPrefix + fieldName
    const dateOfBirthMaxDate = useMemo(() => addYears(new Date(), -16), [])

    const ShowData = (props: { data?: string }) => {
        if (!props.data) {
            return null
        }

        return (
            <>
                {props.data}
                <br />
            </>
        )
    }

    return (
        <div className="acquiring-contact-fields">
            <div className={cn({ "d-none": showReview })}>
                {showGender && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                        <ConditionalLabel title={t("common.gender.label")} useInputLabels={useInputLabels}>
                            <DropDownList<EGender | "">
                                options={[
                                    { text: t("common.gender.male"), value: "Male" },
                                    { text: t("common.gender.female"), value: "Female" },
                                    { text: t("common.gender.nonBinary"), value: "NonBinary" },
                                ]}
                                placeholder={useInputLabels ? "" : t("common.gender.label")}
                                name={getFieldSelector("gender")}
                                disabled={isDisabled}
                                innerRef={control.register(
                                    filterShouldValidate(
                                        valFuncRequired(t("common.gender.errorMessage")),
                                        getFieldSelector("gender"),
                                        validatedFields
                                    )
                                )}
                            ></DropDownList>
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
                {nameType === "fullName" && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                        <ConditionalLabel title={t("common.fullName.label")} useInputLabels={useInputLabels}>
                            <FloatingLabelInput
                                placeholder={t("common.fullName.label")}
                                name={getFieldSelector("name")}
                                type="text"
                                disabled={isDisabled}
                                innerRef={control.register(
                                    filterShouldValidate(
                                        valFuncRequiredAndPattern(validationPatterns.fullName, t("common.fullName.errorMessage")),
                                        getFieldSelector("name"),
                                        validatedFields
                                    )
                                )}
                                defaultValue={userData?.user.fullName}
                                hidePlaceholder={useInputLabels}
                            ></FloatingLabelInput>
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
                {nameType === "firstAndLastName" && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                        <ConditionalLabel title={t("common.firstName.label")} useInputLabels={useInputLabels}>
                            <FloatingLabelInput
                                placeholder={t("common.firstName.label")}
                                name={getFieldSelector("firstName")}
                                type="text"
                                disabled={isDisabled}
                                innerRef={control.register(
                                    filterShouldValidate(
                                        valFuncRequiredAndPattern(
                                            validationPatterns.firstName,
                                            t("common.firstName.errorMessage")
                                        ),
                                        getFieldSelector("firstName"),
                                        validatedFields
                                    )
                                )}
                                defaultValue={userData?.user.fullName}
                                hidePlaceholder={useInputLabels}
                            ></FloatingLabelInput>
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
                {nameType === "firstAndLastName" && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                        <ConditionalLabel title={t("common.lastName.label")} useInputLabels={useInputLabels}>
                            <FloatingLabelInput
                                placeholder={t("common.lastName.label")}
                                name={getFieldSelector("lastName")}
                                type="text"
                                disabled={isDisabled}
                                innerRef={control.register(
                                    filterShouldValidate(
                                        valFuncRequiredAndPattern(validationPatterns.lastName, t("common.lastName.errorMessage")),
                                        getFieldSelector("lastName"),
                                        validatedFields
                                    )
                                )}
                                defaultValue={userData?.user.fullName}
                                hidePlaceholder={useInputLabels}
                            ></FloatingLabelInput>
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
                {showEmail && (
                    <FormInputColumnWithInfoColumn
                        infoColumn={emailHintText && <TitleWithAbsoluteHint title={emailHintText} iconExclamation />}
                    >
                        <FieldsetFormGroup field>
                            <ConditionalLabel title={t("common.email.label")} useInputLabels={useInputLabels}>
                                <FloatingLabelInput
                                    placeholder={t("common.email.label")}
                                    name={getFieldSelector("email")}
                                    type="email"
                                    disabled={isDisabled || isEmailDisabled}
                                    innerRef={control.register(
                                        filterShouldValidate(
                                            valFuncRequiredAndPattern(validationPatterns.email, t("common.email.errorMessage")),
                                            getFieldSelector("email"),
                                            validatedFields
                                        )
                                    )}
                                    defaultValue={userData?.user.email}
                                    hidePlaceholder={useInputLabels}
                                ></FloatingLabelInput>
                            </ConditionalLabel>
                        </FieldsetFormGroup>
                    </FormInputColumnWithInfoColumn>
                )}
                {showPhone && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field mb-0" field>
                        <ConditionalLabel title={t("common.phone.label")} useInputLabels={useInputLabels}>
                            <PhoneNumberInput
                                inputName={getFieldSelector("phone")}
                                defaultValue={userData?.user.phone}
                                register={control.register}
                                required={!validatedFields || validatedFields.includes(getFieldSelector("phone"))}
                                setValue={control.setValue}
                                getValues={control.getValues}
                                watch={control.watchInternal}
                                onValidPhoneTyped={() => control.trigger(getFieldSelector("phone") as FieldName<TForm>)}
                                isDisabled={isDisabled}
                                hidePlaceholder={useInputLabels}
                            />
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
                {showDateOfBirth && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                        <ConditionalLabel
                            title={t("clearhausonboarding.inputfields.dateOfBirth")}
                            useInputLabels={useInputLabels}
                        >
                            <DateInput
                                required
                                name={getFieldSelector("dateOfBirth")}
                                placeholder={useInputLabels ? "" : t("clearhausonboarding.inputfields.dateOfBirth")}
                                errorMessage={t("clearhausonboarding.inputfields.errormsg.dateOfBirth")}
                                formControl={control}
                                valueDateFormat={"yyyy-MM-dd"}
                                addFormatToPlaceholder
                                maxDate={dateOfBirthMaxDate}
                                helpTypeSpecialChars
                            />
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
                {showCountry && (
                    <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                        <ConditionalLabel title={t("personal.country")} useInputLabels={useInputLabels}>
                            <DropDownList
                                defaultValue="DK"
                                placeholder={useInputLabels ? "" : t("personal.country")}
                                name={getFieldSelector("country")}
                                options={getCountriesList()}
                                innerRef={control.register(
                                    filterShouldValidate(
                                        valFuncRequiredAndPattern(
                                            validationPatterns.countryISO2Code,
                                            t("personal.validationerrors.country")
                                        ),
                                        getFieldSelector("country"),
                                        validatedFields
                                    )
                                )}
                            />
                        </ConditionalLabel>
                    </FieldsetFormGroup>
                )}
            </div>
            {showReview && (
                <CompletedFieldsReview>
                    {showGender && <ShowData data={savedFields?.gender || currentFields?.gender} />}
                    {nameType === "fullName" && (
                        <ShowData
                            data={
                                (savedFields as IAcquiringContactFullNameSubForm)?.name ||
                                (currentFields as IAcquiringContactFullNameSubForm)?.name
                            }
                        />
                    )}
                    {nameType === "firstAndLastName" && (
                        <ShowData
                            data={
                                getFullNameFromFirstAndLastName(
                                    (savedFields as IAcquiringContactFirstAndLastNameSubForm)?.firstName,
                                    (savedFields as IAcquiringContactFirstAndLastNameSubForm)?.lastName
                                ) ||
                                getFullNameFromFirstAndLastName(
                                    (currentFields as IAcquiringContactFirstAndLastNameSubForm)?.firstName,
                                    (currentFields as IAcquiringContactFirstAndLastNameSubForm)?.lastName
                                )
                            }
                        />
                    )}
                    {showEmail && <ShowData data={savedFields?.email || currentFields?.email} />}
                    {showPhone && <ShowData data={savedFields?.phone || currentFields?.phone} />}
                    {showDateOfBirth && (
                        <ShowData
                            data={
                                savedFields?.dateOfBirth || currentFields?.dateOfBirth
                                    ? formatShortDateWithFormat(
                                          new Date(savedFields?.dateOfBirth || currentFields!.dateOfBirth!),
                                          getStringLocaleDateFormat()
                                      )
                                    : ""
                            }
                        />
                    )}
                    {showCountry && <ShowData data={savedFields?.country || currentFields?.country} />}
                </CompletedFieldsReview>
            )}
        </div>
    )
}

const ConditionalLabel = ({
    children,
    title,
    useInputLabels,
}: {
    children: React.ReactNode
    title: string
    useInputLabels: boolean | undefined
}) => (useInputLabels ? <InputLabel title={title}>{children}</InputLabel> : children)
